import { Spinner } from '../../../components/spinner/spinner';
import Container from '../../../components/container/container';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import Icon from '../../../components/icons/icon';
import { Typography } from '../../../components/typography/typography';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { Button } from '../../../components/form/button';
import { FieldLabel } from '../../../components/field/field';
import { SelectForm } from '../../../components/select-form/select-form';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { useContext, useEffect, useState } from 'react';
import { Report, ScheduledReport } from '../../../clients/quipli-service/report/report';
import { toast } from '../../../util/toasts';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { Duration } from 'luxon';
import { TenantContext } from '../../../context/tenant-context';

export const ScheduleReport = () => {
  const { scheduledReportId } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(true),
    [scheduledReport, setScheduledReport] = useState<ScheduledReport | null>(),
    [isEditing, setIsEditing] = useState<boolean>(!scheduledReportId),
    [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState<boolean>(false),
    [reports, setReports] = useState<Report[]>([]),
    [reportId, setReportId] = useState<string | undefined>(scheduledReportId),
    [reportDuration, setReportDuration] = useState<{ value?: number; unit?: { id: string; label: string } }>({
      value: 1,
      unit: { id: 'D', label: 'Days' },
    });

  const { updateScheduledReport, scheduleDailyReport, deleteScheduledReport, getScheduledReport, listReports } =
    useReportApiV2();
  const navigate = useNavigate();
  const { contextPath } = useContext(TenantContext);

  const load = async () => {
    setIsLoading(true);
    if (scheduledReportId) {
      try {
        const _scheduledReport = await getScheduledReport(scheduledReportId);
        console.log(_scheduledReport);
        const _reports = await listReports();
        setReports(_reports);
        setScheduledReport(_scheduledReport);
        setReportId(_scheduledReport.report.id);
        setReportDuration({
          value: _scheduledReport.deliveryInterval.as('days'),
          unit: { id: 'D', label: 'Days' },
        });
      } catch (e: any) {
        console.error(`Error loading scheduled report ${e}`);
        toast('Error loading scheduled report', 'error');
      }
    } else {
      setScheduledReport(null);
      const _reports = await listReports();
      setReports(_reports);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const isSaveDisabled = (): boolean => {
    return !reportId;
  };

  const save = async () => {
    if (isSaveDisabled()) return;
    try {
      if (scheduledReportId && reportDuration.value && reportDuration.unit && reportId) {
        await updateScheduledReport(scheduledReportId, {
          deliveryInterval: Duration.fromISO(`P${reportDuration.value}${reportDuration.unit.id}`),
        });
      } else if (scheduledReportId == null && reportDuration.unit && reportDuration.value && reportId) {
        await scheduleDailyReport({
          reportId,
          deliveryInterval: Duration.fromISO(`P${reportDuration.value}${reportDuration.unit.id}`),
        });
      }
      navigate(`${contextPath}manager/reports/dailySchedule`);
    } catch (e: any) {
      console.error(e);
      toast('Failed to save scheduled report', 'error');
    }
  };

  const removeReport = async () => {
    try {
      if (scheduledReportId) {
        await deleteScheduledReport(scheduledReportId);
        toast('Scheduled report deleted', 'success');
        setShowConfirmDeleteModal(false);
      }
    } catch (e: any) {
      console.error(e);
      toast('Failed to delete scheduled report', 'error');
    }
  };

  return (
    <div className="report-schedule p-2 md:py-10 md:px-[5rem]">
      {isLoading || scheduledReport === undefined ? (
        <Spinner />
      ) : (
        <Container className="p-3 md:p-10 scheduled-report-container">
          <div className="flex justify-between items-start">
            <div className="flex gap-5 items-end">
              <NavLink to={`/manager/reports/dailySchedule`}>
                <Icon icon="Back" />
              </NavLink>
              <Typography type="display-xs" weight="bold">
                {isEditing && !scheduledReportId ? 'Add Report to Daily Email' : 'Scheduled Report'}
              </Typography>
            </div>
            {!isEditing && scheduledReportId && (
              <div>
                <Dropdown
                  trigger={() => (
                    <Button className="button-tertiary">
                      <Icon icon="DotsVertical" />
                    </Button>
                  )}
                >
                  <div
                    className="items-center gap-2"
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    <Icon icon="Edit" size="medium" />
                    <Typography type="text-sm" className="text-quipli-black-800">
                      Edit
                    </Typography>
                  </div>
                  <div
                    className="items-center gap-2"
                    onClick={() => {
                      setShowConfirmDeleteModal(true);
                    }}
                  >
                    <Icon icon="Trash" size="medium" />
                    <Typography type="text-sm" className="text-quipli-black-800">
                      Delete
                    </Typography>
                  </div>
                </Dropdown>
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-y-3">
            {/* Report */}
            <div className={`flex gap-5 items-start ${!isEditing ? 'disabled' : ''}`}>
              <FieldLabel label="Type" isRequired={isEditing} />
              {isEditing ? (
                <SelectForm
                  menuShouldBlockScroll
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  options={reports}
                  value={reports.find((t) => t.id === reportId)}
                  onChange={(option) => setReportId(option?.id ?? '')}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  isLoading={isLoading}
                  isDisabled={isLoading || !isEditing}
                  isClearable={true}
                />
              ) : (
                <Typography type="text-sm">{reports.find((t) => t.id === reportId)?.name}</Typography>
              )}
            </div>

            {/* Recurrence
            <div>
              <FieldLabel label="Deliver report every: " isRequired={isEditing} />
              <div className="flex gap-4">
                <div className="mx-6 max-w-[50%] ">
                  <Typography className="p-0 m-0 text-gray-500" type="text-xs">
                    Duration
                  </Typography>
                  {isEditing ? (
                    <div className="flex gap-2 items-center align-middle p-0 m-0">
                      <Field
                        type="number"
                        isRequired={isEditing}
                        value={reportDuration?.value}
                        onChange={(v) => setReportDuration({ ...reportDuration, value: v })}
                      />

                      <SelectForm
                        isRequired={false}
                        menuShouldBlockScroll
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        options={[
                          { id: 'D', label: 'Days' },
                          { id: 'W', label: 'Weeks' },
                          { id: 'M', label: 'Months' },
                          { id: 'Y', label: 'Years' },
                        ]}
                        value={reportDuration?.unit}
                        onChange={(option) => {
                          if (option) setReportDuration({ ...reportDuration, unit: option });
                        }}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => option.label}
                      />
                    </div>
                  ) : (
                    <Typography className="text-gray-800" type="text-sm">
                      {reportDuration.value} {reportDuration.unit?.label}
                    </Typography>
                  )}
                </div>
              </div>
            </div>*/}

            {isEditing && (
              <div className="mt-4 flex justify-between">
                <button
                  className="button button-small button-block button-secondary"
                  disabled={isLoading}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>

                <button
                  className="button button-small button-block button-primary"
                  disabled={isSaveDisabled()}
                  onClick={() => save()}
                >
                  Save
                </button>
              </div>
            )}

            <ConfirmModal
              isOpen={showConfirmDeleteModal}
              additionalText="You will no longer receive reports of this type."
              prompt="Are you sure you want to delete this scheduled report?"
              onConfirm={removeReport}
              close={() => setShowConfirmDeleteModal(false)}
            />
          </div>
        </Container>
      )}
    </div>
  );
};
